'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Client {
  id: number;
  name: string;
  logo: string;
  alt: string;
  industry: string;
}

interface Certification {
  id: number;
  name: string;
  issuer: string;
  icon: string;
  color: string;
}

const TrustIndicators = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const clients: Client[] = [
  {
    id: 1,
    name: "Global Mining Corp",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1922556ec-1765199479532.png",
    alt: "Global Mining Corp logo with mountain peak and pickaxe symbol in gold and navy",
    industry: "Mining"
  },
  {
    id: 2,
    name: "African Resources Ltd",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_155e64c19-1767280273975.png",
    alt: "African Resources Ltd logo featuring stylized continent outline in golden gradient",
    industry: "Extractives"
  },
  {
    id: 3,
    name: "Continental EPC Partners",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e827f263-1767280274922.png",
    alt: "Continental EPC Partners logo with construction crane and gear icon in navy blue",
    industry: "Engineering"
  },
  {
    id: 4,
    name: "East Africa Trade Alliance",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1031dfab0-1767280275050.png",
    alt: "East Africa Trade Alliance logo showing handshake symbol with golden border",
    industry: "Trade"
  },
  {
    id: 5,
    name: "Precious Metals Group",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1a38baf22-1767280274370.png",
    alt: "Precious Metals Group logo with gold bar icon and shield emblem",
    industry: "Mining"
  },
  {
    id: 6,
    name: "Infrastructure Development Co",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12e16e84e-1767280275307.png",
    alt: "Infrastructure Development Co logo featuring bridge and road symbols in golden yellow",
    industry: "Construction"
  }];


  const certifications: Certification[] = [
  {
    id: 1,
    name: "ISO 9001:2015",
    issuer: "Quality Management",
    icon: "CheckBadgeIcon",
    color: "from-blue-400 to-blue-600"
  },
  {
    id: 2,
    name: "ISO 14001",
    issuer: "Environmental",
    icon: "ShieldCheckIcon",
    color: "from-emerald-400 to-emerald-600"
  },
  {
    id: 3,
    name: "OHSAS 18001",
    issuer: "Safety Standards",
    icon: "HeartIcon",
    color: "from-rose-400 to-rose-600"
  },
  {
    id: 4,
    name: "C-TPAT",
    issuer: "Security Certified",
    icon: "LockClosedIcon",
    color: "from-amber-400 to-amber-600"
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.ceil(clients.length / 3));
    }, 4000);

    return () => clearInterval(interval);
  }, [isHydrated, clients.length]);

  const visibleClients = clients.slice(currentIndex * 3, currentIndex * 3 + 3);

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        {/* Trusted By Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-golden-light/10 via-golden-mid/10 to-golden-dark/10 rounded-full mb-4">
              <Icon name="StarIcon" size={20} className="text-golden-mid" />
              <span className="font-cta font-semibold text-sm text-primary">Trusted Partners</span>
            </div>
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-3">
              Trusted by Industry Leaders
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Serving Africa's premier mining and logistics companies
            </p>
          </div>

          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {visibleClients.map((client) =>
              <div
                key={client.id}
                className="bg-card rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-border group">

                  <div className="aspect-video relative rounded-lg overflow-hidden mb-4 bg-gray-100">
                    <AppImage
                    src={client.logo}
                    alt={client.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                  </div>
                  <h3 className="font-headline font-bold text-lg text-foreground mb-1">
                    {client.name}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground">
                    {client.industry}
                  </p>
                </div>
              )}
            </div>

            {/* Carousel Indicators */}
            {isHydrated &&
            <div className="flex items-center justify-center space-x-2 mt-6">
                {Array.from({ length: Math.ceil(clients.length / 3) }).map((_, index) =>
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ?
                'w-8 bg-gradient-to-r from-golden-light to-golden-dark' : 'w-2 bg-border hover:bg-golden-light'}`
                }
                aria-label={`Go to slide ${index + 1}`} />

              )}
              </div>
            }
          </div>
        </div>

        {/* Certifications Section */}
        <div>
          <div className="text-center mb-8">
            <h3 className="font-headline font-bold text-2xl lg:text-3xl text-foreground mb-3">
              Industry Certifications
            </h3>
            <p className="font-body text-base text-muted-foreground">
              Maintaining the highest standards of quality, safety, and compliance
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert) =>
            <div
              key={cert.id}
              className="bg-card rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-border text-center group">

                <div className={`w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gradient-to-br ${cert.color} rounded-full shadow-md group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={cert.icon as any} size={32} className="text-white" />
                </div>
                <h4 className="font-headline font-bold text-lg text-foreground mb-1">
                  {cert.name}
                </h4>
                <p className="font-body text-sm text-muted-foreground">
                  {cert.issuer}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default TrustIndicators;